import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useEstimationQuery } from "@/hooks/queries/estimates/useGetEstimateQuery";
import { useGetEstimateDetailQuery } from "@/hooks/queries/estimates/useGetEstimateDetailQuery";
import { useUpdateEstimateFieldMutation } from "@/hooks/mutation/estimates/useUpdateEstimateFieldMutation";
import { ESTIMATE_KEYS } from "@/hooks/queries/estimates/queryKeys";
import { estimationSelector } from "@/redux/selectors/estimation.selector";
import { estimationActions } from "@/redux/actions/estimation.actions";
import { useDispatch, useSelector } from "@/redux/store";
import type { Estimation } from "@/interfaces/estimation.interface";

export function useDetailsSectionData(estimateId: string) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { data: estimationRaw } = useEstimationQuery(estimateId);
  const estimation = estimationRaw ?? undefined;
  const { data: estimateDetailData } = useGetEstimateDetailQuery(estimateId);

  const files = estimateDetailData?.files ?? [];
  const notes = estimation?.notes ?? [];
  const links = estimation?.links ?? [];

  const rightSidebarCurrentTab = useSelector(estimationSelector.rightSidebarCurrentTab);
  const currentRoom = useSelector(estimationSelector.currentRoom);
  const lineItemDetail = useSelector(estimationSelector.lineItemDetail);
  const fullPageImagePath = useSelector(estimationSelector.fullPageImagePath);

  const { mutateAsync: updateEstimateField } = useUpdateEstimateFieldMutation();

  const updateLinks = useCallback(
    async (_links: string[]) => {
      const previousEstimation = queryClient.getQueryData<Estimation | null>(ESTIMATE_KEYS.detailMetadata(estimateId));

      if (previousEstimation) {
        const updatedEstimation = { ...previousEstimation, links: _links };
        // Update TanStack Query cache
        queryClient.setQueryData(ESTIMATE_KEYS.detailMetadata(estimateId), updatedEstimation);
        // Also update Redux
        dispatch(estimationActions.setEstimationState({ links: _links }));
      }

      try {
        await updateEstimateField({
          estimateId,
          updates: { links: _links },
        });
        return true;
      } catch {
        // Revert both caches on error
        queryClient.setQueryData(ESTIMATE_KEYS.detailMetadata(estimateId), previousEstimation);
        if (previousEstimation) {
          dispatch(estimationActions.setEstimationState({ links: previousEstimation.links ?? [] }));
        }
        return false;
      }
    },
    [dispatch, estimateId, queryClient, updateEstimateField],
  );

  const unSelectLineItem = useCallback(() => {
    dispatch(
      estimationActions.setEstimationState({
        rightSidebarCurrentTab: { value: "Details", id: "Details" },
        lineItemDetail: undefined,
      }),
    );
  }, [dispatch]);

  return {
    estimation,
    notes,
    links,
    files,
    estimateDetailData,
    rightSidebarCurrentTab,
    currentRoom,
    lineItemDetail,
    fullPageImagePath,
    updateLinks,
    unSelectLineItem,
  };
}
